import {
  MaterialReactTable,
  MRT_ActionMenuItem,
  MRT_ColumnDef,
  MRT_RowSelectionState,
  useMaterialReactTable,
} from 'material-react-table';
import { FC, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AddIcon from '@/assets/add.svg';
import EditIcon from '@/assets/edit.svg';
import ConfirmAnnouncementModal from '@/components/modals/confirm-announcement';
import ConfirmResponseNotificationModal from '@/components/modals/confirm-response-notification';
import { Button } from '@/components/ui/button';
import { Drawer, DrawerProps } from '@/components/ui/drawer';
import { HeadingGroup } from '@/components/ui/heading-group';
import { Modal } from '@/components/ui/modal';
import { strings } from '@/constants/strings';
import { useCloseInvalidDrawer } from '@/hooks/use-close-invalid-drawer';
import { useCurrentOrganisation } from '@/hooks/use-current-organisation';
import { useGroups } from '@/hooks/use-groups';
import { useOpenState } from '@/hooks/use-open-state';
import { useOrganisationSelectOptions } from '@/hooks/use-organisation-select-options';
import { useOrganisations } from '@/hooks/use-organisations';
import { useUnits } from '@/hooks/use-units';
import { useUser } from '@/hooks/use-user';
import { useUserRole } from '@/hooks/use-user-role';
import { HeadingGroupContainerLayout } from '@/layouts/heading-group-container-layout';
import { SendAnnouncementForm } from '@/pages/authenticated/devices/drawer-views/send-announcement-form';
import { api } from '@/redux/api';
import {
  composeTableOptions,
  getEntityRowId,
  getFilteredSelectedRows,
} from '@/table';
import { Group } from '@/types/group';
import { findById } from '@/utils/find-by-id';
import { isUserTechnician } from '@/utils/users/is-user-technician';
import { CreateGroupForm } from './drawer-views/create-group-form';
import { EditGroupForm } from './drawer-views/edit-group-form';
import { SelectedGroupsActionBar } from './selected-groups-action-bar';

type OpenContext = { groupId: number };

/**
 * We can create or edit groups in the drawer, or send announcements to all units
 * in the selected groups.
 */
type DrawerView = 'create' | 'edit' | 'announce' | 'responseNotification';
type DrawerContext = { view: DrawerView } & Partial<OpenContext> & {
    groups?: Group[];
  };
type AnnouncementConfirmationContext = {
  groups: Group[];
  notificationText: string;
};
type ResponseNotificationConfirmationContext = {
  groups: Group[];
  notificationText: string;
};

export const GroupsPage: FC = () => {
  const navigate = useNavigate();
  const { user: activeUser } = useUser();
  const { isPlatformAdmin } = useUserRole();
  const isTechnician = isUserTechnician(activeUser);

  const {
    data: groups,
    isFetching: isFetchingGroups,
    isUninitialized: isGroupsUninitialized,
    createGroup,
    updateGroup,
  } = useGroups();
  const isLoadingGroups = isGroupsUninitialized || isFetchingGroups;
  const [deleteGroup] = api.useDeleteGroupMutation();

  /**
   * The Units hook returns all of our units across all of our groups in the
   * context of our current organisation.
   */
  const { organisation, isUsingOrganisationOverride } =
    useCurrentOrganisation();
  const { data: units } = useUnits();

  /**
   * Get organisations for platform admins
   */
  const { data: organisations } = useOrganisations();
  const organisationOptions = useOrganisationSelectOptions(organisations);

  /**
   * Determine if the user can assign any organisation
   */
  const canAssignAnyOrganisation =
    isPlatformAdmin && !isUsingOrganisationOverride;

  /**
   * Track the selected groups in our table
   */
  const [rowSelection, setRowSelection] = useState<MRT_RowSelectionState>({});

  // Drawer state handling
  const {
    state: drawerState,
    open: openDrawer,
    toggleOpen: toggleDrawerOpen,
  } = useOpenState<DrawerContext>({ view: 'create' });

  // Delete confirmation modal state
  const {
    state: deleteConfirmationState,
    open: openDeleteConfirmation,
    toggleOpen: toggleDeleteConfirmationOpen,
  } = useOpenState<OpenContext>({ groupId: -1 });

  // Announcement confirmation modal state
  const {
    state: announcementConfirmationState,
    open: openAnnouncementConfirmation,
    toggleOpen: toggleAnnouncementConfirmationOpen,
  } = useOpenState<AnnouncementConfirmationContext>({
    groups: [],
    notificationText: '',
  });

  // Response Notification confirmation modal state
  const {
    state: responseNotificationConfirmationState,
    open: openResponseNotificationConfirmation,
    toggleOpen: toggleResponseNotificationConfirmationOpen,
  } = useOpenState<ResponseNotificationConfirmationContext>({
    groups: [],
    notificationText: '',
  });

  /**
   * From our selected groups, get all the units that belong to those groups
   */
  const getUnitsForGroups = (selectedGroups: Group[]) => {
    if (!selectedGroups.length) return [];
    return units.filter(
      (unit) =>
        unit.group &&
        selectedGroups.some((group) => group.id === unit.group?.id),
    );
  };

  /**
   * From our selected groups, get all the unit IDs
   */
  const getUnitIdsForGroups = (selectedGroups: Group[]): number[] =>
    getUnitsForGroups(selectedGroups).map((unit) => unit.id);

  const columns: MRT_ColumnDef<Group>[] = useMemo(() => {
    const baseColumns: MRT_ColumnDef<Group>[] = [
      { accessorKey: 'groupName', header: 'Group Name' },
    ];

    // Add organization column for platform admins
    if (isPlatformAdmin) {
      baseColumns.push({
        id: 'organisation',
        accessorFn: ({ organisation }) => organisation?.name,
        header: 'Organisation',
        size: 150,
      });
    }

    return baseColumns;
  }, [isPlatformAdmin]);

  const table = useMaterialReactTable({
    ...composeTableOptions<Group>({
      enableRowActions: true,
      enableRowSelection: true,
    }),
    columns,
    enableColumnResizing: true,
    enableColumnFilters: true,
    enableFacetedValues: true,
    enableStickyHeader: true,
    data: groups || [],
    getRowId: getEntityRowId,
    state: {
      isLoading: isLoadingGroups,
      showColumnFilters: true,
      rowSelection,
    },
    onRowSelectionChange: setRowSelection,
    muiTableBodyRowProps: ({ row }) => ({
      onClick: (event) => {
        // Prevent navigation when clicking on action buttons or checkboxes
        if (!(event.target as HTMLElement).closest('.MuiButtonBase-root')) {
          navigate(`${row.original.id}`);
        }
      },
      sx: { cursor: 'pointer' },
    }),
    renderRowActionMenuItems: ({ row, table, closeMenu }) => {
      if (isTechnician) return [];

      return [
        <MRT_ActionMenuItem
          icon={<EditIcon />}
          key="edit"
          label="Edit"
          onClick={() => {
            closeMenu();
            openDrawer({ view: 'edit', groupId: row.original.id });
          }}
          table={table}
        />,
      ];
    },
  });

  const selectedGroups = getFilteredSelectedRows(table);

  const selectedGroupsHaveUnitsWithDevices = getUnitsForGroups(
    selectedGroups,
  ).some((unit) => unit.devices && unit.devices.length > 0);

  const getDrawerTitleProps = (): Pick<DrawerProps, 'title'> => {
    switch (drawerState.context.view) {
      case 'create':
        return { title: 'Create New Group' };
      case 'edit':
        return { title: 'Edit Group' };
      case 'announce':
        return { title: 'Announcement' };
      default:
        return { title: '' };
    }
  };

  const drawerGroup = findById(groups, drawerState.context.groupId ?? -1);
  const drawerHasContent =
    drawerState.context.view === 'create' ||
    (drawerState.context.view === 'edit' && Boolean(drawerGroup)) ||
    ((drawerState.context.view === 'announce' ||
      drawerState.context.view === 'responseNotification') &&
      Boolean(drawerState.context.groups?.length));
  useCloseInvalidDrawer(drawerState.open, drawerHasContent, toggleDrawerOpen);

  const renderDrawerContent = () => {
    switch (drawerState.context.view) {
      case 'create':
        return (
          <CreateGroupForm
            canAssignAnyOrganisation={canAssignAnyOrganisation}
            onCancel={() => toggleDrawerOpen(false)}
            onSubmit={(data) => {
              const organisationId = canAssignAnyOrganisation
                ? typeof data.organisationId === 'number'
                  ? data.organisationId
                  : undefined
                : organisation?.id;

              if (organisationId) {
                createGroup({
                  organisationId,
                  groupName: data.groupName,
                });
                toggleDrawerOpen(false);
              }
            }}
            organisationName={organisation?.name ?? ''}
            organisationOptions={organisationOptions}
          />
        );
      case 'edit': {
        const group = drawerGroup;
        if (group) {
          return (
            <EditGroupForm
              group={group}
              onCancel={() => toggleDrawerOpen(false)}
              onDelete={() => openDeleteConfirmation({ groupId: group.id })}
              onSubmit={(data) => {
                updateGroup({
                  id: group.id,
                  groupName: data.groupName,
                });
                toggleDrawerOpen(false);
              }}
            />
          );
        }
        break;
      }
      case 'announce': {
        const { groups } = drawerState.context;
        if (groups && groups.length > 0) {
          return (
            <SendAnnouncementForm
              onCancel={() => toggleDrawerOpen(false)}
              onSubmit={(data) => {
                openAnnouncementConfirmation({
                  groups,
                  notificationText: data.notificationText,
                });
              }}
            />
          );
        }
        break;
      }
      case 'responseNotification': {
        const { groups } = drawerState.context;
        if (groups && groups.length > 0) {
          return (
            <SendAnnouncementForm
              onCancel={() => toggleDrawerOpen(false)}
              onSubmit={(data) => {
                openResponseNotificationConfirmation({
                  groups,
                  notificationText: data.notificationText,
                });
              }}
            />
          );
        }
        break;
      }
      default:
        break;
    }

    return null;
  };

  return (
    <>
      <HeadingGroupContainerLayout>
        <HeadingGroup
          subtitlePostfix="Management"
          subtitlePrefix="Organisation"
          subtitleTooltip="Groups"
          title="Groups"
          tooltip={strings.groups.tooltip}
        />
        {!isTechnician && (
          <Button onClick={() => openDrawer({ view: 'create' })}>
            <AddIcon /> Add Group
          </Button>
        )}
      </HeadingGroupContainerLayout>

      {/* Action bar for selected groups */}
      <SelectedGroupsActionBar
        hasUnitsWithDevices={selectedGroupsHaveUnitsWithDevices}
        openSendAnnouncement={() =>
          openDrawer({ view: 'announce', groups: selectedGroups })
        }
        openSendResponseNotification={() =>
          openDrawer({ view: 'responseNotification', groups: selectedGroups })
        }
        selectedGroups={selectedGroups}
      />

      <MaterialReactTable table={table} />

      <Drawer
        {...getDrawerTitleProps()}
        open={drawerState.open}
        setOpen={toggleDrawerOpen}
      >
        {renderDrawerContent()}
      </Drawer>

      {/* Delete confirmation modal */}
      <Modal
        buttonItems={[
          <Button
            key="cancel"
            onClick={() => toggleDeleteConfirmationOpen(false)}
            variant="secondary"
          >
            Cancel
          </Button>,
          <Button
            key="delete"
            onClick={() => {
              deleteGroup({ id: deleteConfirmationState.context.groupId });
              toggleDeleteConfirmationOpen(false);
            }}
            variant="primary-warning"
          >
            Delete
          </Button>,
        ]}
        description="Are you sure you want to delete this group? This action cannot be undone."
        open={deleteConfirmationState.open}
        setOpen={toggleDeleteConfirmationOpen}
        title="Delete Group"
      />

      {/* Announcement confirmation modal */}
      <ConfirmAnnouncementModal
        isOpen={announcementConfirmationState.open}
        notificationText={
          announcementConfirmationState.context.notificationText
        }
        onCancel={() => toggleAnnouncementConfirmationOpen(false)}
        onSend={() => {
          toggleAnnouncementConfirmationOpen(false);
          toggleDrawerOpen(false);
          setRowSelection({}); // Clear selection after sending
        }}
        unitIds={getUnitIdsForGroups(
          announcementConfirmationState.context.groups,
        )}
      />
      <ConfirmResponseNotificationModal
        isOpen={responseNotificationConfirmationState.open}
        notificationText={
          responseNotificationConfirmationState.context.notificationText
        }
        onCancel={() => toggleResponseNotificationConfirmationOpen(false)}
        onSend={() => {
          toggleResponseNotificationConfirmationOpen(false);
          toggleDrawerOpen(false);
          setRowSelection({});
        }}
        unitIds={getUnitIdsForGroups(
          responseNotificationConfirmationState.context.groups,
        )}
      />
    </>
  );
};
